import toast from 'react-hot-toast';

class NotificationManager {
  constructor() {
    this.activeNotifications = new Map();
    this.notificationQueue = [];
    this.isProcessingQueue = false;
    this.maxConcurrentNotifications = 3;
    this.notificationTimeout = 6000;
  }

  // Generate unique notification ID
  generateNotificationId(type, data = {}) {
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substr(2, 9);
    
    switch (type) {
      case 'new-order':
        return `new-order-${data.orderId || data.order?.id}-${timestamp}`;
      case 'order-status-update':
        return `status-update-${data.orderId}-${data.status}-${timestamp}`;
      case 'vendor-notification':
        return `vendor-${data.id || timestamp}-${randomId}`;
      case 'user-action':
        return `action-${data.action}-${timestamp}-${randomId}`;
      default:
        return `notification-${type}-${timestamp}-${randomId}`;
    }
  }

  // Check if notification already exists
  isNotificationActive(notificationId) {
    return this.activeNotifications.has(notificationId);
  }

  // Add notification to queue
  addToQueue(notification) {
    this.notificationQueue.push(notification);
    this.processQueue();
  }

  // Process notification queue
  async processQueue() {
    if (this.isProcessingQueue || this.notificationQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.notificationQueue.length > 0 && this.activeNotifications.size < this.maxConcurrentNotifications) {
      const notification = this.notificationQueue.shift();
      
      if (!this.isNotificationActive(notification.id)) {
        await this.showNotification(notification);
      }
    }

    this.isProcessingQueue = false;

    // Continue processing if there are more notifications in queue
    if (this.notificationQueue.length > 0) {
      setTimeout(() => this.processQueue(), 1000);
    }
  }

  // Show notification with proper styling
  async showNotification(notification) {
    const toastId = toast(
      notification.content,
      {
        id: notification.id,
        duration: notification.duration || this.notificationTimeout,
        position: 'top-right',
        style: {
          background: notification.style?.background || '#ffffff',
          border: notification.style?.border || '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '16px',
          minWidth: notification.style?.minWidth || '300px',
          maxWidth: notification.style?.maxWidth || '400px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          cursor: notification.onClick ? 'pointer' : 'default',
        },
        ...notification.options
      }
    );

    // Track active notification
    this.activeNotifications.set(notification.id, {
      toastId,
      timestamp: Date.now(),
      type: notification.type
    });

    // Auto-remove from tracking after duration
    setTimeout(() => {
      this.activeNotifications.delete(notification.id);
    }, notification.duration || this.notificationTimeout);
  }

  // Show new order notification
  showNewOrderNotification(orderData) {
    const notificationId = this.generateNotificationId('new-order', { orderId: orderData.id });
    
    if (this.isNotificationActive(notificationId)) {
      return;
    }

    const notification = {
      id: notificationId,
      type: 'new-order',
      content: (
        <div 
          className="flex items-start space-x-3 cursor-pointer"
          onClick={() => {
            window.location.href = `/orders?order=${orderData.id}&action=view`;
            this.dismissNotification(notificationId);
          }}
        >
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-lg">🆕</span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-green-800">New Order Received!</div>
            <div className="text-sm text-green-700">Order #{orderData.id}</div>
            <div className="text-sm font-medium text-green-800">${orderData.total}</div>
            <div className="text-xs text-green-600 mt-1">
              {orderData.orderItems?.length || 0} items • {orderData.order_type}
            </div>
            {orderData.orderItems && orderData.orderItems.length > 0 && (
              <div className="mt-1 text-xs text-green-700">
                <div className="font-medium">Items:</div>
                                 {orderData.orderItems.slice(0, 2).map((item, idx) => (
                   <div key={idx} className="text-xs">
                     {item.quantity}x {item.menuItem?.name || 'Unknown Item'}
                     {item.addons && item.addons.length > 0 && (
                       <span className="text-green-600"> (+{item.addons.length} addons)</span>
                     )}
                   </div>
                 ))}
                {orderData.orderItems.length > 2 && (
                  <div className="text-xs text-green-600">
                    +{orderData.orderItems.length - 2} more items
                  </div>
                )}
              </div>
            )}
            <div className="text-xs text-green-600 mt-2 font-medium">
              Click to view order details →
            </div>
          </div>
        </div>
      ),
      duration: 8000,
      style: {
        background: '#f0fdf4',
        border: '1px solid #bbf7d0',
        minWidth: '320px',
      }
    };

    this.addToQueue(notification);
  }

  // Show order status update notification
  showStatusUpdateNotification(orderId, status, orderData) {
    const notificationId = this.generateNotificationId('order-status-update', { orderId, status });
    
    if (this.isNotificationActive(notificationId)) {
      return;
    }

    const statusColors = {
      'confirmed': { bg: 'blue', color: 'blue' },
      'preparing': { bg: 'orange', color: 'orange' },
      'ready': { bg: 'green', color: 'green' },
      'completed': { bg: 'green', color: 'green' },
      'cancelled': { bg: 'red', color: 'red' }
    };
    
    const color = statusColors[status] || { bg: 'gray', color: 'gray' };
    
    const notification = {
      id: notificationId,
      type: 'order-status-update',
      content: (
        <div 
          className="flex items-start space-x-3 cursor-pointer"
          onClick={() => {
            window.location.href = `/orders?order=${orderId}&action=status`;
            this.dismissNotification(notificationId);
          }}
        >
          <div className="flex-shrink-0">
            <div className={`w-8 h-8 bg-${color.bg}-100 rounded-full flex items-center justify-center`}>
              <span className={`text-${color.color}-600 text-lg`}>📝</span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`font-semibold text-${color.color}-800`}>Order Status Updated</div>
            <div className="text-sm text-gray-700">Order #{orderId}</div>
            <div className={`text-sm font-medium text-${color.color}-800 capitalize`}>
              Status: {status.replace('_', ' ')}
            </div>
            {orderData && orderData.orderItems && orderData.orderItems.length > 0 && (
              <div className="mt-1 text-xs text-gray-600">
                <div className="font-medium">Items:</div>
                                 {orderData.orderItems.slice(0, 2).map((item, idx) => (
                   <div key={idx} className="text-xs">
                     {item.quantity}x {item.menuItem?.name || 'Unknown Item'}
                     {item.addons && item.addons.length > 0 && (
                       <span className="text-gray-500"> (+{item.addons.length} addons)</span>
                     )}
                   </div>
                 ))}
                {orderData.orderItems.length > 2 && (
                  <div className="text-xs text-gray-500">
                    +{orderData.orderItems.length - 2} more items
                  </div>
                )}
              </div>
            )}
            <div className={`text-xs text-${color.color}-600 mt-2 font-medium`}>
              Click to view order →
            </div>
          </div>
        </div>
      ),
      duration: 6000,
      style: {
        background: `var(--${color.bg}-50)`,
        border: `1px solid var(--${color.bg}-200)`,
        minWidth: '300px',
      }
    };

    this.addToQueue(notification);
  }

  // Show vendor notification
  showVendorNotification(message, data = {}) {
    const notificationId = this.generateNotificationId('vendor-notification', data);
    
    if (this.isNotificationActive(notificationId)) {
      return;
    }

    const notification = {
      id: notificationId,
      type: 'vendor-notification',
      content: (
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-lg">📢</span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-blue-800">Vendor Notification</div>
            <div className="text-sm text-blue-700">{message}</div>
          </div>
        </div>
      ),
      duration: 6000,
      style: {
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        minWidth: '300px',
      }
    };

    this.addToQueue(notification);
  }

  // Show user action notification (success/error)
  showUserActionNotification(type, message, action = 'general') {
    const notificationId = this.generateNotificationId('user-action', { action, type });
    
    // For user actions, we allow multiple notifications but with different content
    const uniqueId = `${notificationId}-${message}`;
    
    if (this.isNotificationActive(uniqueId)) {
      return;
    }

    const notification = {
      id: uniqueId,
      type: 'user-action',
      content: (
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className={`w-8 h-8 ${type === 'success' ? 'bg-green-100' : 'bg-red-100'} rounded-full flex items-center justify-center`}>
              <span className={`text-lg ${type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                {type === 'success' ? '✓' : '✕'}
              </span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`font-semibold ${type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
              {type === 'success' ? 'Success' : 'Error'}
            </div>
            <div className={`text-sm ${type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
              {message}
            </div>
          </div>
        </div>
      ),
      duration: type === 'success' ? 4000 : 6000,
      style: {
        background: type === 'success' ? '#f0fdf4' : '#fef2f2',
        border: type === 'success' ? '1px solid #bbf7d0' : '1px solid #fecaca',
        minWidth: '300px',
      }
    };

    this.addToQueue(notification);
  }

  // Dismiss specific notification
  dismissNotification(notificationId) {
    if (this.activeNotifications.has(notificationId)) {
      toast.dismiss(this.activeNotifications.get(notificationId).toastId);
      this.activeNotifications.delete(notificationId);
    }
  }

  // Clear all notifications
  clearAllNotifications() {
    toast.dismiss();
    this.activeNotifications.clear();
    this.notificationQueue = [];
    this.isProcessingQueue = false;
  }

  // Get active notifications count
  getActiveNotificationsCount() {
    return this.activeNotifications.size;
  }

  // Get queue length
  getQueueLength() {
    return this.notificationQueue.length;
  }
}

// Create singleton instance
const notificationManager = new NotificationManager();

export default notificationManager; 